// src/pages/returnBatch/ReturnBatchDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Package,
  ArrowLeft,
  Plus,
  Printer,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  User,
  Phone,
  Store,
  MapPin,
  CreditCard,
  Truck,
  Calendar,
  FileText,
  Eye,
  Search,
  Filter,
  Clock,
  Box,
  TrendingUp,
  DollarSign,
  List,
  CheckSquare,
  Activity,
  Boxes
} from 'lucide-react';
import {
  getReturnBatchById,
  getReturnBatchProductList,
  closeReturnBatch
} from '../../services/api/returnBatch';
import { exportProductListToExcel, exportProductListToPDF } from '../../utils/packingBatchProductExport';
import ProductListPrintModal from '../packingBatch/components/ProductListPrintModal';
import { usePermissions } from '../../hooks/usePermissions';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { formatCurrency } from '../../utils/orderCalculations';
import { formatDate, formatTime, formatDateTime } from '../../utils/dateUtils';
import styles from './ReturnBatchDetailsPage.module.css';

const ReturnBatchDetailsPage = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error: showError } = useAlert();

  const [batch, setBatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [isClosing, setIsClosing] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showStockSummary, setShowStockSummary] = useState(false);

  // Orders / Product list toggle
  const [viewMode, setViewMode] = useState('orders');
  const [productList, setProductList] = useState(null);
  const [isLoadingProductList, setIsLoadingProductList] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductPrintModal, setShowProductPrintModal] = useState(false);

  useEffect(() => {
    loadBatchDetails();
  }, [batchId]);

  const loadBatchDetails = async () => {
    try {
      setLoading(true);
      const response = await getReturnBatchById(batchId);
      setBatch(response.returnBatch);
      // Batch contents may have changed — force the product list to refetch next time it's viewed
      setProductList(null);
    } catch (error) {
      console.error('Failed to load batch details:', error);
      showError(error.response?.data?.message || t('returnBatch.failedToLoadBatchDetails'));
    } finally {
      setLoading(false);
    }
  };

  const loadProductList = async () => {
    try {
      setIsLoadingProductList(true);
      const response = await getReturnBatchProductList(batchId);
      setProductList(response);
    } catch (error) {
      console.error('Failed to load product list:', error);
      showError(error.response?.data?.message || t('returnBatch.loadProductListError'));
    } finally {
      setIsLoadingProductList(false);
    }
  };

  useEffect(() => {
    if (viewMode === 'products' && !productList && !isLoadingProductList) {
      loadProductList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, productList]);

  const handleExportProductListExcel = () => {
    if (!productList) return;
    const result = exportProductListToExcel(productList.products, batch.batchNumber, 'return-batch');
    if (result.success) success(result.message);
    else showError(result.message);
  };

  const handleExportProductListPDF = () => {
    if (!productList) return;
    const result = exportProductListToPDF(productList.products, batch.batchNumber, 'return-batch');
    if (result.success) success(result.message);
    else showError(result.message);
  };

  // Builds "Product × qty, Product × qty" for an order's items
  const buildItemsDescription = (orderItems = [], separator = ', ') => {
    if (!orderItems || orderItems.length === 0) return '—';
    return orderItems
      .map(item => {
        const name = item.Product?.name || t('returnBatch.unknown');
        const qty = parseFloat(item.quantity) % 1 === 0
          ? parseInt(item.quantity)
          : parseFloat(item.quantity);
        return `${name} × ${qty}`;
      })
      .join(separator);
  };

  const handleCloseBatch = async () => {
    if (!window.confirm(t('returnBatch.confirmCloseBatch').replace('{batchNumber}', batch.batchNumber))) {
      return;
    }

    try {
      setIsClosing(true);
      await closeReturnBatch(batchId);
      success(t('returnBatch.batchClosedSuccessfully').replace('{batchNumber}', batch.batchNumber));
      await loadBatchDetails();
    } catch (error) {
      console.error('Failed to close batch:', error);
      showError(error.response?.data?.message || t('returnBatch.failedToCloseBatch'));
    } finally {
      setIsClosing(false);
    }
  };

  const handleCopyAirwaybill = (airwaybillNumber) => {
    navigator.clipboard.writeText(airwaybillNumber);
    setCopiedId(airwaybillNumber);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const toggleOrderExpansion = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const handlePrintReport = () => {
    window.print();
  };

  const handleExportOrders = () => {
    if (!batch) return;

    const csvHeaders = [
      t('returnBatch.orderNumber'),
      t('returnBatch.airwaybillNumber'),
      t('returnBatch.customerName'),
      t('returnBatch.phone'),
      t('returnBatch.deliveryAddress'),
      t('returnBatch.city'),
      t('returnBatch.shop'),
      t('returnBatch.orderItems'),
      t('returnBatch.paymentType'),
      t('returnBatch.orderValue'),
      t('returnBatch.codAmount'),
      t('returnBatch.status'),
      t('returnBatch.stockRestored'),
      t('returnBatch.returnedBy'),
      t('returnBatch.returnedAt'),
      t('returnBatch.notes')
    ];

    const csvRows = batch.returnOrders.map(ro => {
      const order = ro.EcomOrder;
      const phones = order.phoneNumbers?.map(p => p.phoneNumber).join('; ') || '';
      
      return [
        order.orderNumber,
        ro.airwaybillNumber || '',
        order.customerName,
        phones,
        order.address,
        order.City?.name || '',
        order.Shop?.name || '',
        order.orderItems?.length ? buildItemsDescription(order.orderItems, '; ') : '',
        order.PaymentType?.label || '',
        order.grandTotal,
        order.codAmount,
        order.OrderStatus?.label || '',
        ro.stockRestored ? t('returnBatch.restored') : t('returnBatch.notRestored'),
        ro.returnedBy?.name || '',
        formatDateTime(ro.returnedAt),
        ro.notes || ''
      ].map(field => `"${String(field).replace(/"/g, '""')}"`);
    });

    const csvContent = [
      csvHeaders.join(','),
      ...csvRows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `return-batch-${batch.batchNumber}-orders.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    success(t('returnBatch.ordersExportedSuccessfully'));
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return styles.statusActive;
      case 'closed':
        return styles.statusClosed;
      default:
        return styles.statusDefault;
    }
  };

  const filteredOrders = batch?.returnOrders?.filter(returnOrder => {
    const order = returnOrder.EcomOrder;
    const matchesSearch = !searchTerm || 
      order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      returnOrder.airwaybillNumber?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || returnOrder.status === statusFilter;

    return matchesSearch && matchesStatus;
  }) || [];

  const filteredProducts = (productList?.products || []).filter(product => {
    if (!productSearchTerm) return true;
    const term = productSearchTerm.toLowerCase();
    return (
      product.displayName.toLowerCase().includes(term) ||
      (product.sku || '').toLowerCase().includes(term)
    );
  });

  // Calculate statistics
  const statistics = batch ? {
    totalOrders: batch.totalOrders,
    totalValue: parseFloat(batch.totalValue || 0),
    avgOrderValue: batch.totalOrders > 0 ? parseFloat(batch.totalValue || 0) / batch.totalOrders : 0,
    codOrders: batch.returnOrders.filter(ro => ro.EcomOrder.PaymentType?.key === 'cod').length,
    prepaidOrders: batch.returnOrders.filter(ro => ro.EcomOrder.PaymentType?.key === 'prepaid').length,
    stockRestored: batch.returnOrders.filter(ro => ro.stockRestored).length,
    paymentBreakdown: {},
    shopBreakdown: {}
  } : null;

  if (batch) {
    // Calculate payment type breakdown
    batch.returnOrders.forEach(ro => {
      const paymentType = ro.EcomOrder.PaymentType?.label || t('returnBatch.unknown');
      if (!statistics.paymentBreakdown[paymentType]) {
        statistics.paymentBreakdown[paymentType] = { count: 0, value: 0 };
      }
      statistics.paymentBreakdown[paymentType].count += 1;
      statistics.paymentBreakdown[paymentType].value += parseFloat(ro.orderValue || 0);
    });

    // Calculate shop breakdown
    batch.returnOrders.forEach(ro => {
      const shopName = ro.EcomOrder.Shop?.name || t('returnBatch.unknown');
      if (!statistics.shopBreakdown[shopName]) {
        statistics.shopBreakdown[shopName] = { count: 0, value: 0 };
      }
      statistics.shopBreakdown[shopName].count += 1;
      statistics.shopBreakdown[shopName].value += parseFloat(ro.orderValue || 0);
    });
  }

  // Aggregate stock movements
  const stockMovements = batch ? batch.returnOrders.reduce((acc, ro) => {
    // This would need to be enhanced if stock movement data is available in the API response
    return acc;
  }, {}) : {};

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Loader2 size={48} className={styles.spinning} />
        <p>{t('returnBatch.loadingBatchDetails')}</p>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className={styles.errorContainer}>
        <AlertCircle size={48} />
        <h3>{t('returnBatch.batchNotFound')}</h3>
        <button onClick={() => navigate('/return-batches')} className={styles.backButton}>
          <ArrowLeft size={18} />
          {t('returnBatch.backToBatches')}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.detailsPage}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <button onClick={() => navigate('/return-batches')} className={styles.backButton}>
          <ArrowLeft size={18} />
          {t('returnBatch.back')}
        </button>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Package className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('returnBatch.returnBatchDetails')} #{batch.batchNumber}</h1>
              <div className={`${styles.statusBadge} ${getStatusBadgeClass(batch.status)}`}>
                {batch.status === 'active' ? <Clock size={14} /> : <CheckCircle size={14} />}
                <span>{batch.status === 'active' ? t('returnBatch.statusActive') : t('returnBatch.statusClosed')}</span>
              </div>
            </div>
          </div>
          <div className={styles.headerActions}>
            {batch.status === 'active' && hasPermission('process_returns') && (
              <>
                <Link
                  to={`/return-batches/process?batchId=${batchId}`}
                  className={styles.actionButton}
                >
                  <Plus size={18} />
                  {t('returnBatch.addMoreOrders')}
                </Link>
                <button 
                  onClick={handleCloseBatch}
                  disabled={isClosing}
                  className={`${styles.actionButton} ${styles.closeActionButton}`}
                >
                  {isClosing ? (
                    <>
                      <Loader2 size={18} className={styles.spinning} />
                      {t('returnBatch.closing')}
                    </>
                  ) : (
                    <>
                      <CheckSquare size={18} />
                      {t('returnBatch.closeBatch')}
                    </>
                  )}
                </button>
              </>
            )}
            <button onClick={handlePrintReport} className={styles.actionButton}>
              <Printer size={18} />
              {t('returnBatch.printReport')}
            </button>
            <button onClick={handleExportOrders} className={styles.actionButton}>
              <Download size={18} />
              {t('returnBatch.exportOrders')}
            </button>
          </div>
        </div>
      </div>

      {/* Batch Info Cards */}
      <div className={styles.infoCards}>
        <div className={styles.infoCard}>
          <div className={styles.cardIcon}>
            <Calendar size={20} />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('returnBatch.created')}</span>
            <span className={styles.cardValue}>
              {formatDate(batch.createdAt)}
            </span>
            <span className={styles.cardSubtext}>
              {formatTime(batch.createdAt)}
            </span>
            <span className={styles.cardSubtext}>
              {t('returnBatch.by')} {batch.createdBy?.name || t('returnBatch.unknown')}
            </span>
          </div>
        </div>

        {batch.status === 'closed' && batch.closedAt && (
          <div className={styles.infoCard}>
            <div className={styles.cardIcon}>
              <CheckCircle size={20} />
            </div>
            <div className={styles.cardContent}>
              <span className={styles.cardLabel}>{t('returnBatch.closed')}</span>
              <span className={styles.cardValue}>
                {formatDate(batch.closedAt)}
              </span>
              <span className={styles.cardSubtext}>
                {formatTime(batch.closedAt)}
              </span>
              <span className={styles.cardSubtext}>
                {t('returnBatch.by')} {batch.closedBy?.name || t('returnBatch.unknown')}
              </span>
            </div>
          </div>
        )}

        <div className={styles.infoCard}>
          <div className={styles.cardIcon}>
            <MapPin size={20} />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('returnBatch.warehouse')}</span>
            <span className={styles.cardValue}>{batch.Warehouse?.name || t('returnBatch.na')}</span>
            <span className={styles.cardSubtext}>{batch.Warehouse?.address || ''}</span>
          </div>
        </div>

        <div className={styles.infoCard}>
          <div className={styles.cardIcon}>
            <Truck size={20} />
          </div>
          <div className={styles.cardContent}>
            <span className={styles.cardLabel}>{t('returnBatch.courierAccount')}</span>
            <span className={styles.cardValue}>
              {batch.courierAccount?.accountName || t('returnBatch.na')}
            </span>
            <span className={styles.cardSubtext}>
              {batch.CourierCompany?.name || ''}
            </span>
          </div>
        </div>
      </div>

      {/* Statistics Summary Cards */}
      <div className={styles.summaryCards}>
        <div className={`${styles.summaryCard} ${styles.totalCard}`}>
          <div className={styles.summaryIcon}>
            <Package size={24} />
          </div>
          <div className={styles.summaryContent}>
            <h3>{statistics.totalOrders}</h3>
            <p>{t('returnBatch.totalOrders')}</p>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.valueCard}`}>
          <div className={styles.summaryIcon}>
            <DollarSign size={24} />
          </div>
          <div className={styles.summaryContent}>
            <h3>{formatCurrency(statistics.totalValue)}</h3>
            <p>{t('returnBatch.totalValue')}</p>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.avgCard}`}>
          <div className={styles.summaryIcon}>
            <TrendingUp size={24} />
          </div>
          <div className={styles.summaryContent}>
            <h3>{formatCurrency(statistics.avgOrderValue)}</h3>
            <p>{t('returnBatch.avgOrderValue')}</p>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.codCard}`}>
          <div className={styles.summaryIcon}>
            <CreditCard size={24} />
          </div>
          <div className={styles.summaryContent}>
            <h3>{statistics.codOrders}</h3>
            <p>{t('returnBatch.codOrders')}</p>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.prepaidCard}`}>
          <div className={styles.summaryIcon}>
            <CheckCircle size={24} />
          </div>
          <div className={styles.summaryContent}>
            <h3>{statistics.prepaidOrders}</h3>
            <p>{t('returnBatch.prepaidOrders')}</p>
          </div>
        </div>

        <div className={`${styles.summaryCard} ${styles.stockCard}`}>
          <div className={styles.summaryIcon}>
            <Box size={24} />
          </div>
          <div className={styles.summaryContent}>
            <h3>{statistics.stockRestored}</h3>
            <p>{t('returnBatch.stockRestored')}</p>
          </div>
        </div>
      </div>

      {/* Notes Section */}
      {batch.notes && (
        <div className={styles.notesSection}>
          <FileText size={18} />
          <div className={styles.notesContent}>
            <strong>{t('returnBatch.batchNotes')}:</strong> {batch.notes}
          </div>
        </div>
      )}

      {/* Breakdown Sections */}
      <div className={styles.breakdownSection}>
        <div className={styles.breakdownCard}>
          <h3>
            <CreditCard size={18} />
            {t('returnBatch.paymentTypeBreakdown')}
          </h3>
          <div className={styles.breakdownList}>
            {Object.entries(statistics.paymentBreakdown).map(([type, data]) => (
              <div key={type} className={styles.breakdownItem}>
                <span className={styles.breakdownLabel}>{type}</span>
                <div className={styles.breakdownValues}>
                  <span className={styles.breakdownCount}>{data.count} {t('returnBatch.orders').toLowerCase()}</span>
                  <span className={styles.breakdownValue}>{formatCurrency(data.value)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {Object.keys(statistics.shopBreakdown).length > 1 && (
          <div className={styles.breakdownCard}>
            <h3>
              <Store size={18} />
              {t('returnBatch.shopBreakdown')}
            </h3>
            <div className={styles.breakdownList}>
              {Object.entries(statistics.shopBreakdown).map(([shop, data]) => (
                <div key={shop} className={styles.breakdownItem}>
                  <span className={styles.breakdownLabel}>{shop}</span>
                  <div className={styles.breakdownValues}>
                    <span className={styles.breakdownCount}>{data.count} {t('returnBatch.orders').toLowerCase()}</span>
                    <span className={styles.breakdownValue}>{formatCurrency(data.value)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Timeline Toggle */}
      <div className={styles.toggleSection}>
        <button
          onClick={() => setShowTimeline(!showTimeline)}
          className={styles.toggleButton}
        >
          <Activity size={18} />
          <span>{t('returnBatch.activityTimeline')}</span>
          {showTimeline ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>

      {/* Timeline */}
      {showTimeline && (
        <div className={styles.timelineSection}>
          <div className={styles.timeline}>
            <div className={styles.timelineItem}>
              <div className={styles.timelineIcon}>
                <Plus size={16} />
              </div>
              <div className={styles.timelineContent}>
                <div className={styles.timelineTitle}>{t('returnBatch.batchCreated')}</div>
                <div className={styles.timelineDetails}>
                  {t('returnBatch.createdBy')} {batch.createdBy?.name || t('returnBatch.unknown')}
                </div>
                <div className={styles.timelineDate}>
                  {formatDateTime(batch.createdAt)}
                </div>
              </div>
            </div>

            {batch.returnOrders
              .sort((a, b) => new Date(a.returnedAt) - new Date(b.returnedAt))
              .map((ro) => (
                <div key={ro.id} className={styles.timelineItem}>
                  <div className={styles.timelineIcon}>
                    <Package size={16} />
                  </div>
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineTitle}>
                      {t('returnBatch.orderAdded').replace('{orderNumber}', ro.EcomOrder.orderNumber)}
                    </div>
                    <div className={styles.timelineDetails}>
                      {t('returnBatch.addedBy')} {ro.returnedBy?.name || t('returnBatch.unknown')}
                      {ro.notes && ` • ${ro.notes}`}
                    </div>
                    <div className={styles.timelineDate}>
                      {formatDateTime(ro.returnedAt)}
                    </div>
                  </div>
                </div>
              ))}

            {batch.status === 'closed' && batch.closedAt && (
              <div className={styles.timelineItem}>
                <div className={styles.timelineIcon}>
                  <CheckCircle size={16} />
                </div>
                <div className={styles.timelineContent}>
                  <div className={styles.timelineTitle}>{t('returnBatch.closeBatch')}</div>
                  <div className={styles.timelineDetails}>
                    {t('returnBatch.closedBy')} {batch.closedBy?.name || t('returnBatch.unknown')}
                  </div>
                  <div className={styles.timelineDate}>
                    {formatDateTime(batch.closedAt)}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Orders Table */}
      <div className={styles.ordersSection}>
        <div className={styles.sectionHeader}>
          <div className={styles.sectionHeaderTitle}>
            <h2>
              {viewMode === 'orders'
                ? `${t('returnBatch.ordersInBatch')} (${filteredOrders.length})`
                : t('returnBatch.productListTitle', { count: filteredProducts.length })}
            </h2>
            <div className={styles.viewToggle}>
              <button
                className={`${styles.viewToggleButton} ${viewMode === 'orders' ? styles.viewToggleActive : ''}`}
                onClick={() => setViewMode('orders')}
              >
                <List size={16} />
                {t('returnBatch.viewOrdersTab')}
              </button>
              <button
                className={`${styles.viewToggleButton} ${viewMode === 'products' ? styles.viewToggleActive : ''}`}
                onClick={() => setViewMode('products')}
              >
                <Boxes size={16} />
                {t('returnBatch.viewProductsTab')}
              </button>
            </div>
          </div>

          {viewMode === 'orders' ? (
            <div className={styles.tableControls}>
              <div className={styles.searchBox}>
                <Search size={18} />
                <input
                  type="text"
                  placeholder={t('returnBatch.searchOrders')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={styles.statusSelect}
              >
                <option value="all">{t('returnBatch.allStatus')}</option>
                <option value="success">{t('returnBatch.success')}</option>
                <option value="failed">{t('returnBatch.failed')}</option>
                <option value="pending">{t('returnBatch.pending')}</option>
              </select>
            </div>
          ) : (
            <div className={styles.tableControls}>
              <div className={styles.searchBox}>
                <Search size={18} />
                <input
                  type="text"
                  placeholder={t('returnBatch.searchProductsPlaceholder')}
                  value={productSearchTerm}
                  onChange={(e) => setProductSearchTerm(e.target.value)}
                />
              </div>
              <button
                onClick={() => setShowProductPrintModal(true)}
                className={styles.filterButton}
                disabled={!productList}
              >
                <Printer size={16} />
                {t('returnBatch.printProductList')}
              </button>
              <button
                onClick={handleExportProductListExcel}
                className={styles.filterButton}
                disabled={!productList}
              >
                <Download size={16} />
                {t('returnBatch.exportExcel')}
              </button>
              <button
                onClick={handleExportProductListPDF}
                className={styles.filterButton}
                disabled={!productList}
              >
                <Download size={16} />
                {t('returnBatch.exportPdf')}
              </button>
            </div>
          )}
        </div>

        {viewMode === 'orders' ? (
        <>
        <div className={styles.tableContainer}>
          <table className={styles.ordersTable}>
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>{t('returnBatch.orderNumber')}</th>
                <th>{t('returnBatch.airwaybill')}</th>
                <th>{t('returnBatch.customer')}</th>
                <th>{t('returnBatch.phone')}</th>
                <th>{t('returnBatch.city')}</th>
                <th>{t('returnBatch.shop')}</th>
                <th>{t('returnBatch.items')}</th>
                <th>{t('returnBatch.payment')}</th>
                <th>{t('returnBatch.orderValue')}</th>
                <th>{t('returnBatch.codAmount')}</th>
                <th>{t('returnBatch.stock')}</th>
                <th>{t('returnBatch.returnedBy')}</th>
                <th>{t('returnBatch.returnedAt')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((returnOrder) => {
                const order = returnOrder.EcomOrder;
                const isExpanded = expandedOrders.has(returnOrder.id);
                
                return (
                  <React.Fragment key={returnOrder.id}>
                    <tr className={styles.orderRow}>
                      <td>
                        <button
                          onClick={() => toggleOrderExpansion(returnOrder.id)}
                          className={styles.expandButton}
                        >
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      </td>
                      <td>
                        <div className={styles.orderNumber}>
                          #{order.orderNumber}
                        </div>
                      </td>
                      <td>
                        {returnOrder.airwaybillNumber ? (
                          <div className={styles.airwaybillCell}>
                            <span className={styles.airwaybillNumber}>
                              {returnOrder.airwaybillNumber}
                            </span>
                            <button
                              onClick={() => handleCopyAirwaybill(returnOrder.airwaybillNumber)}
                              className={styles.copyButton}
                              title={t('returnBatch.copyAirwaybill')}
                            >
                              {copiedId === returnOrder.airwaybillNumber ? (
                                <Check size={14} className={styles.checkIcon} />
                              ) : (
                                <Copy size={14} />
                              )}
                            </button>
                          </div>
                        ) : (
                          <span className={styles.noAirwaybill}>-</span>
                        )}
                      </td>
                      <td>
                        <div className={styles.customerInfo}>
                          <User size={12} />
                          {order.customerName}
                        </div>
                      </td>
                      <td>
                        <div className={styles.phoneInfo}>
                          <Phone size={12} />
                          {order.phoneNumbers?.[0]?.phoneNumber || t('returnBatch.na')}
                        </div>
                      </td>
                      <td>
                        <div className={styles.cityInfo}>
                          <MapPin size={12} />
                          {order.City?.name || t('returnBatch.na')}
                        </div>
                      </td>
                      <td>
                        <div className={styles.shopBadge}>
                          <Store size={12} />
                          {order.Shop?.name || t('returnBatch.na')}
                        </div>
                      </td>
                      <td>
                        <div
                          className={styles.itemsDescription}
                          title={buildItemsDescription(order.orderItems)}
                        >
                          {buildItemsDescription(order.orderItems)}
                        </div>
                      </td>
                      <td>
                        <div className={styles.paymentBadge}>
                          {order.PaymentType?.label || t('returnBatch.na')}
                        </div>
                      </td>
                      <td>
                        <div className={styles.totalAmount}>
                          {formatCurrency(order.grandTotal)}
                        </div>
                      </td>
                      <td>
                        <div className={styles.codAmount}>
                          {formatCurrency(order.codAmount)}
                        </div>
                      </td>
                      <td>
                        <div className={styles.stockStatus}>
                          {returnOrder.stockRestored ? (
                            <div className={styles.stockRestored}>
                              <CheckCircle size={14} />
                              <span>{t('returnBatch.restored')}</span>
                            </div>
                          ) : (
                            <div className={styles.stockNotRestored}>
                              <XCircle size={14} />
                              <span>{t('returnBatch.notRestored')}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <div className={styles.returnedByInfo}>
                          {returnOrder.returnedBy?.name || t('returnBatch.unknown')}
                        </div>
                      </td>
                      <td>
                        <div className={styles.dateInfo}>
                          {formatDate(returnOrder.returnedAt)}
                        </div>
                        <div className={styles.timeInfo}>
                          {formatTime(returnOrder.returnedAt)}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className={styles.expandedRow}>
                        <td colSpan="14">
                          <div className={styles.expandedContent}>
                            <div className={styles.expandedSection}>
                              <h4>
                                <MapPin size={16} />
                                {t('returnBatch.deliveryAddress')}
                              </h4>
                              <p>{order.address}</p>
                              <p className={styles.locationDetails}>
                                {order.City?.name}
                                {order.City?.District?.name && `, ${order.City.District.name}`}
                                {order.City?.District?.Province?.name && `, ${order.City.District.Province.name}`}
                              </p>
                            </div>

                            {order.phoneNumbers && order.phoneNumbers.length > 0 && (
                              <div className={styles.expandedSection}>
                                <h4>
                                  <Phone size={16} />
                                  {t('returnBatch.contactNumbers')}
                                </h4>
                                <div className={styles.phoneList}>
                                  {order.phoneNumbers.map((phone, idx) => (
                                    <div key={idx} className={styles.phoneItem}>
                                      <Phone size={12} />
                                      <span>{phone.phoneNumber}</span>
                                      {phone.isPrimary && (
                                        <span className={styles.primaryBadge}>{t('returnBatch.primary')}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className={styles.expandedSection}>
                              <h4>
                                <List size={16} />
                                {t('returnBatch.orderStatus')}
                              </h4>
                              <div className={styles.statusInfo}>
                                <span className={styles.statusLabel}>{t('returnBatch.currentStatus')}:</span>
                                <span className={styles.statusValue}>
                                  {order.OrderStatus?.label || t('returnBatch.unknown')}
                                </span>
                              </div>
                            </div>

                            {returnOrder.notes && (
                              <div className={styles.expandedSection}>
                                <h4>
                                  <FileText size={16} />
                                  {t('returnBatch.returnNotes')}
                                </h4>
                                <p>{returnOrder.notes}</p>
                              </div>
                            )}

                            {returnOrder.errorMessage && (
                              <div className={styles.expandedSection}>
                                <h4>
                                  <AlertCircle size={16} />
                                  {t('returnBatch.errorMessage')}
                                </h4>
                                <p className={styles.errorText}>{returnOrder.errorMessage}</p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredOrders.length === 0 && (
          <div className={styles.emptyState}>
            <Package size={48} />
            <h3>{t('returnBatch.noOrdersFound')}</h3>
            <p>{t('returnBatch.noOrdersMatchFilters')}</p>
          </div>
        )}
        </>
        ) : isLoadingProductList ? (
          <div className={styles.loadingContainer}>
            <Loader2 size={32} className={styles.spinning} />
            <p>{t('returnBatch.loadingProductList')}</p>
          </div>
        ) : (
          <>
            <div className={styles.tableContainer}>
              <table className={`${styles.ordersTable} ${styles.productsTable}`}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>{t('returnBatch.productColumn')}</th>
                    <th>{t('returnBatch.skuColumn')}</th>
                    <th>{t('returnBatch.totalQuantityColumn')}</th>
                    <th>{t('returnBatch.orderCountColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((product, index) => (
                    <tr key={`${product.productId}-${product.variationId || 'base'}`}>
                      <td>{index + 1}</td>
                      <td>
                        <div className={styles.orderNumber}>{product.displayName}</div>
                      </td>
                      <td>{product.sku || t('returnBatch.na')}</td>
                      <td>
                        <div className={styles.totalAmount}>{product.totalQuantity}</div>
                      </td>
                      <td>{product.ordersCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredProducts.length === 0 && (
              <div className={styles.emptyState}>
                <Package size={48} />
                <h3>{t('returnBatch.noProductsFound')}</h3>
                <p>{t('returnBatch.noProductsFoundDescription')}</p>
              </div>
            )}
          </>
        )}
      </div>

      {showProductPrintModal && productList && (
        <ProductListPrintModal
          products={productList.products}
          batchNumber={batch.batchNumber}
          onClose={() => setShowProductPrintModal(false)}
          onError={showError}
        />
      )}
    </div>
  );
};

export default ReturnBatchDetailsPage;